import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";
const API_URL = `${API_BASE_URL}/jobs/public/`;
const APPLY_URL = (id) => `${API_BASE_URL}/job-requests/${id}/apply/`;


function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const STATUS_STYLES = {
  pending:   { bg: '#fffde0', color: '#8a7f00', border: '#fff000' },
  assigned:  { bg: '#eaf6ea', color: '#1a7a1a', border: '#7fc97f' },
  accepted:  { bg: '#f2f2f2', color: '#000000', border: '#cccccc' },
  completed: { bg: '#f2f2f2', color: '#000000', border: '#cccccc' },
  default:   { bg: '#f1f3f5', color: '#6c757d', border: '#e0e0e0' },
};

function StatusPill({ status }) {
  const key = (status || '').toLowerCase();
  const s = STATUS_STYLES[key] || STATUS_STYLES.default;
  return (
    <span
      style={{
        background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
        borderRadius: 20, padding: '7px 20px', fontWeight: 700,
        fontSize: '.85rem', textTransform: 'capitalize', whiteSpace: 'nowrap',
      }}
    >
      {status || 'open'}
    </span>
  );
}

export default function JobDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job,     setJob]     = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [errorType, setErrorType] = useState(null);

  // Apply Now state
  const [applying, setApplying]   = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applySuccess, setApplySuccess] = useState(false);

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    setLoading(true);
    setErrorType(null);
    try {
      const response = await fetch(`${API_URL}${id}/`);

      if (response.status === 404) {
        setJob(null);
        setErrorType('notfound');
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      setJob(data);
    } catch (err) {
      console.error('JobDetailsPage fetch failed:', err);
      setJob(null);
      setErrorType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplyError(null);
    setApplySuccess(false);
    setApplying(true);

    try {
      const csrfToken = getCsrfToken();
      const response = await fetch(APPLY_URL(id), {
        method: "POST",
        credentials: "include", // sends the httpOnly session/auth cookie
        headers: {
          "Content-Type": "application/json",
          // Django requires this on POST/PATCH/DELETE even with a valid
          // session cookie. If csrfToken is null (e.g. cookie not set yet
          // because the person never loaded a Django-rendered page or
          // hasn't hit an endpoint that sets it), Django will 403 and the
          // branch below surfaces that clearly instead of failing silently.
          ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        // No valid session cookie — not logged in.
        navigate(`/login?next=/jobs/${id}`);
        return;
      }
      if (response.status === 403) {
        setApplyError(data.error || data.detail || "You're not eligible to apply for this job yet.");
        return;
      }
      if (response.status === 400) {
        setApplyError(data.error || "This job is no longer available.");
        // Refresh so the page reflects the current state (e.g. someone else took it).
        fetchJob();
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      // Success — job now carries the updated status/assignment.
      setJob(data);
      setApplySuccess(true);
    } catch (err) {
      console.error('Apply failed:', err);
      setApplyError("Something went wrong applying for this job. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const wrapStyle = {
    background: 'var(--bg)', minHeight: '100vh', fontFamily: "'Raleway',sans-serif",
    color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const sharedStyleTag = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap');
      :root{
        --green:#000000; --green-d:#1a1a1a; --green-l:#f2f2f2;
        --gold:#fff000; --gold-d:#e0d800; --gold-l:#fffde0;
        --bg:#ffffff; --text:#000000; --muted:#4d4d4d;
      }
    `}</style>
  );

  if (loading) {
    return (
      <div style={wrapStyle}>
        {sharedStyleTag}
        <div style={{textAlign:'center'}}>
          <div style={{width:44,height:44,borderRadius:'50%',border:'4px solid var(--green-l)',borderTopColor:'var(--gold)',animation:'kk-spin .8s linear infinite',margin:'0 auto 16px'}}/>
          <style>{`@keyframes kk-spin{to{transform:rotate(360deg);}}`}</style>
          <p style={{fontWeight:600,color:'var(--muted)'}}>Loading job…</p>
        </div>
      </div>
    );
  }

  // Couldn't reach the server — distinct from a confirmed 404, and retryable.
  if (errorType === 'error') {
    return (
      <div style={wrapStyle}>
        {sharedStyleTag}
        <div style={{textAlign:'center',background:'#fff',borderRadius:16,padding:'48px 40px',border:'1.5px solid #e5e5e5',boxShadow:'0 2px 16px rgba(0,0,0,.07)',maxWidth:420}}>
          <span style={{fontSize:'2.5rem',display:'block',marginBottom:14}}>⚠️</span>
          <h2 style={{fontFamily:"'Raleway',sans-serif",fontWeight:800,fontSize:'1.3rem',margin:'0 0 8px'}}>Couldn't Load This Job</h2>
          <p style={{color:'var(--muted)',fontSize:'.9rem',margin:'0 0 20px'}}>
            We couldn't reach the server. Check your connection and try again — the job may still exist.
          </p>
          <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
            <button
              onClick={fetchJob}
              style={{background:'var(--gold)',color:'#000000',border:'none',borderRadius:10,padding:'11px 24px',fontWeight:700,fontSize:'.88rem',cursor:'pointer',fontFamily:"'Raleway',sans-serif"}}
            >
              <i className="fas fa-rotate-right me-2"/>Try Again
            </button>
            <Link to="/jobs" style={{display:'inline-block',background:'var(--green)',color:'#fff',borderRadius:10,padding:'11px 24px',fontWeight:700,fontSize:'.88rem',textDecoration:'none'}}>
              <i className="fas fa-arrow-left me-2"/>Back to Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Server confirmed there's no job at this id.
  if (!job) {
    return (
      <div style={wrapStyle}>
        {sharedStyleTag}
        <div style={{textAlign:'center',background:'#fff',borderRadius:16,padding:'48px 40px',border:'1.5px solid #e5e5e5',boxShadow:'0 2px 16px rgba(0,0,0,.07)'}}>
          <span style={{fontSize:'2.5rem',display:'block',marginBottom:14}}>🔍</span>
          <h2 style={{fontFamily:"'Raleway',sans-serif",fontWeight:800,fontSize:'1.3rem',margin:'0 0 8px'}}>Job Not Found</h2>
          <p style={{color:'var(--muted)',fontSize:'.9rem',margin:'0 0 20px'}}>This job may have been removed or the link is incorrect.</p>
          <Link to="/jobs" style={{display:'inline-block',background:'var(--green)',color:'#fff',borderRadius:10,padding:'11px 26px',fontWeight:700,fontSize:'.88rem',textDecoration:'none'}}>
            <i className="fas fa-arrow-left me-2"/>Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  const jobIsOpen = job.status === 'Pending' || job.status === 'pending';

  return (
    <div style={{background:'var(--bg)',minHeight:'100vh',fontFamily:"'Raleway',sans-serif",color:'var(--text)',padding:'44px 20px 70px'}}>
      {sharedStyleTag}
      <style>{`
        .kk-jd-card{max-width:900px;margin:0 auto;}
        .kk-jd-back{display:inline-flex;align-items:center;gap:7px;color:var(--text);font-weight:700;font-size:.88rem;text-decoration:none;margin-bottom:22px;transition:gap .15s;}
        .kk-jd-back:hover{gap:11px;color:var(--gold-d);}
        .kk-jd-panel{background:#fff;border-radius:20px;box-shadow:0 8px 32px rgba(0,0,0,.08);border:1.5px solid #e5e5e5;overflow:hidden;}
        .kk-jd-hero{background:linear-gradient(135deg,var(--green-d) 0%,var(--green) 100%);padding:36px 34px;position:relative;overflow:hidden;}
        .kk-jd-hero::before{content:'';position:absolute;top:-60px;right:-60px;width:200px;height:200px;border-radius:50%;background:rgba(255,240,0,.1);}
        .kk-jd-hero-top{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-start;gap:16px;position:relative;z-index:1;}
        .kk-jd-tag{display:inline-flex;align-items:center;gap:6px;background:rgba(255,240,0,.16);border:1px solid rgba(255,240,0,.4);color:var(--gold);border-radius:20px;padding:4px 13px;font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.04em;margin-bottom:12px;}
        .kk-jd-title{font-family:'Raleway',sans-serif;font-weight:800;font-size:clamp(1.5rem,3.5vw,2.1rem);color:#fff;margin:0 0 6px;}
        .kk-jd-posted{color:rgba(255,255,255,.78);font-size:.88rem;margin:0;}
        .kk-jd-body{padding:34px;}
        .kk-jd-apply{display:inline-flex;align-items:center;gap:8px;background:var(--gold);color:#000000;border:none;border-radius:12px;padding:14px 30px;font-weight:700;font-size:.93rem;cursor:pointer;transition:all .2s;box-shadow:0 4px 18px rgba(255,240,0,.35);margin-bottom:14px;font-family:'Raleway',sans-serif;}
        .kk-jd-apply:hover:not(:disabled){background:var(--gold-d);transform:translateY(-2px);box-shadow:0 8px 26px rgba(255,240,0,.45);}
        .kk-jd-apply:disabled{opacity:.6;cursor:not-allowed;transform:none;}
        .kk-jd-apply-msg{font-size:.85rem;font-weight:600;margin:0 0 20px;}
        .kk-jd-apply-msg.error{color:#b3261e;}
        .kk-jd-apply-msg.success{color:#1a7a1a;}
        .kk-jd-hr{border:none;border-top:1.5px solid #e5e5e5;margin:0 0 28px;}
        .kk-jd-section-title{font-family:'Raleway',sans-serif;font-weight:800;font-size:1.3rem;color:var(--text);margin:0 0 14px;}
        .kk-jd-desc{color:#333333;line-height:1.9;font-size:.96rem;white-space:pre-line;margin:0 0 34px;}
        .kk-jd-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px;}
        .kk-jd-info-card{background:var(--bg);border:1.5px solid #e5e5e5;border-radius:14px;padding:18px 20px;}
        .kk-jd-info-card.full{grid-column:1/-1;}
        .kk-jd-info-label{display:flex;align-items:center;gap:8px;font-weight:700;font-size:.78rem;color:var(--muted);text-transform:uppercase;letter-spacing:.04em;margin:0 0 8px;}
        .kk-jd-info-label i{color:var(--gold-d);}
        .kk-jd-info-value{font-size:.95rem;color:var(--text);margin:0;font-weight:600;}
        .kk-jd-info-value.money{color:var(--text);font-weight:800;font-size:1.1rem;}
        @media(max-width:576px){
          .kk-jd-hero,.kk-jd-body{padding:26px 20px;}
        }
      `}</style>

      <div className="kk-jd-card">
        <Link to="/jobs" className="kk-jd-back">
          <i className="fas fa-arrow-left"/> Back to Jobs
        </Link>

        <div className="kk-jd-panel">
          <div className="kk-jd-hero">
            <div className="kk-jd-hero-top">
              <div>
                <span className="kk-jd-tag"><i className="fas fa-briefcase"/> Job Listing</span>
                <h1 className="kk-jd-title">{job.service || "General Service"}</h1>
                <p className="kk-jd-posted">Posted by {job.client?.full_name || "Anonymous Client"}</p>
              </div>
              <StatusPill status={job.status}/>
            </div>
          </div>

          <div className="kk-jd-body">
            {jobIsOpen && !applySuccess && (
              <button
                className="kk-jd-apply"
                onClick={handleApply}
                disabled={applying}
              >
                <i className="fas fa-paper-plane"/> {applying ? "Applying…" : "Apply Now"}
              </button>
            )}

            {!jobIsOpen && !applySuccess && (
              <p className="kk-jd-apply-msg" style={{color:'var(--muted)'}}>
                This job has already been taken and is no longer accepting applications.
              </p>
            )}

            {applySuccess && (
              <p className="kk-jd-apply-msg success">
                <i className="fas fa-circle-check me-2"/> You've been assigned this job. Check your dashboard for next steps.
              </p>
            )}

            {applyError && (
              <p className="kk-jd-apply-msg error">{applyError}</p>
            )}

            <hr className="kk-jd-hr"/>

            <h2 className="kk-jd-section-title">Job Description</h2>
            <p className="kk-jd-desc">{job.description || "No description provided."}</p>

            <div className="kk-jd-grid">
              <div className="kk-jd-info-card">
                <p className="kk-jd-info-label"><i className="fas fa-map-marker-alt"/>Location</p>
                <p className="kk-jd-info-value">{job.location || "Not specified"}</p>
              </div>

              <div className="kk-jd-info-card">
                <p className="kk-jd-info-label"><i className="fas fa-coins"/>Budget</p>
                <p className="kk-jd-info-value money">{job.budget || "Negotiable"}</p>
              </div>

              <div className="kk-jd-info-card">
                <p className="kk-jd-info-label"><i className="fas fa-user"/>Client</p>
                <p className="kk-jd-info-value">{job.client?.full_name || "N/A"}</p>
              </div>

              {/* Phone number card intentionally removed — the client's
                  phone is PII and shouldn't be exposed on this public,
                  unauthenticated listing. It becomes visible once a
                  craftsman is actually assigned (see JobRequestSerializer
                  .get_client on the backend). */}

              <div className="kk-jd-info-card full">
                <p className="kk-jd-info-label"><i className="fas fa-info-circle"/>Current Status</p>
                <p className="kk-jd-info-value" style={{textTransform:'capitalize'}}>{job.status}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
